/**
 * Report sections of the foresight checklist modules (#630, specs/foresight-checklist.md).
 * A run is one foresight scenario, and an instance can hold several; every
 * section lists them scenario by scenario: the brief, the change drivers, the
 * scenario's tulevaisuuskuvat, the preferred future, the backcasting path and
 * the wild-card stress tests, and the organisational memory (assumptions,
 * rejected alternatives and their reasons, revealed assumptions and watch signals). Each renders only when the chain includes it and it has content.
 * The data endpoint needs a sign-in (PIN or Entra), like the systemic v2 sections.
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { Section, SectionTitle, SectionSubtitle, Paragraph, Divider, SITRA } from './reportStyles';
import SectionGate from './SectionGate';

export const FORESIGHT_NAV_KEYS = {
  'foresight-brief': 'foresightBrief',
  'change-drivers': 'changeDrivers',
  'foresight-scenarios': 'foresightScenarios',
  'preferred-future': 'preferredFuture',
  'backcasting-path': 'backcastingPath',
  'stress-test': 'stressTest',
  'foresight-assumptions': 'foresightAssumptions',
};
export const FORESIGHT_SECTIONS = Object.keys(FORESIGHT_NAV_KEYS);

const List = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.li`
  padding: 12px 14px;
  border-radius: 4px;
  border: 1px solid ${SITRA.border};
  border-left: 4px solid ${(p) => p.$accent || SITRA.border};
  background: ${SITRA.white};
  color: ${SITRA.textBody};
  line-height: 1.55;
  .title { font-weight: 600; }
  .meta { margin-top: 4px; font-size: 14px; color: ${SITRA.textMuted}; }
  p { margin: 6px 0 0; white-space: pre-wrap; }
  ul { margin: 6px 0 0; padding-left: 20px; }
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: minmax(140px, 220px) 1fr;
  gap: 8px 16px;
  margin: 12px 0 0;
  dt { color: ${SITRA.textMuted}; }
  dd { margin: 0; white-space: pre-wrap; }
  @media (max-width: 600px) { grid-template-columns: 1fr; dd { margin-bottom: 8px; } }
`;

const RunTitle = styled.h3`
  margin: 24px 0 4px;
  font-size: 19px;
  color: ${SITRA.textBody};
`;

const ARCHETYPE_ACCENT = { bau: '#94a3b8', positive: '#2e9e5b', negative: '#c0392b' };
const GROUP_ACCENT = { promote: '#2e9e5b', avoid: '#c0392b', adaptTo: '#d4a017', canInfluence: '#2f6fb3' };
const PATH_GROUPS = ['promote', 'avoid', 'adaptTo', 'canInfluence'];
const STAGES = ['T4', 'T3', 'T2', 'T1', 'T0'];
const ITEM_DETAILS = ['responsibility', 'metric', 'obstacle', 'remedy', 'rationale'];
const BRIEF_FIELDS = ['subjectName', 'description', 'goal', 'currentState', 'challenges', 'whyForesight', 'scopeLimits'];

const approvedFutures = (run) => (run.futures || []).filter((f) => f.status === 'approved');
const startingAssumptions = (run) => (run.brief?.aiAssumptions || []).filter((a) => a.accepted !== false).map((a) => a.text);
const testedCards = (run) => (run.wildcards || []).filter((c) => c.stressTest);
const VERDICT_ACCENT = { holds: '#2e9e5b', adjust: '#d4a017', breaks: '#c0392b' };

/** For each section, whether a run has something to show in it. */
const HAS = {
  'foresight-brief': (r) => Boolean(r.brief?.subjectName),
  'change-drivers': (r) => (r.drivers || []).some((d) => d.status === 'approved'),
  'foresight-scenarios': (r) => approvedFutures(r).length > 0,
  'preferred-future': (r) => Boolean(r.preferredFuture?.description),
  'backcasting-path': (r) => (r.path?.items || []).length > 0,
  'stress-test': (r) => testedCards(r).length > 0,
  'foresight-assumptions': (r) => startingAssumptions(r).length > 0
    || testedCards(r).some((c) => c.stressTest.revealedAssumptions?.length || c.stressTest.watchSignals?.length)
    || approvedFutures(r).some((f) => f.assumptions?.length)
    || (r.drivers || []).some((d) => d.status === 'rejected')
    || (r.futures || []).some((f) => f.status === 'rejected'),
};

/** @param {(id: string) => boolean} enabled whether the chain (and access) includes a section */
export function useForesightReportData(instanceId, enabled) {
  const [runs, setRuns] = useState([]);
  const want = FORESIGHT_SECTIONS.some((id) => enabled(id));
  useEffect(() => {
    if (!instanceId || !want) return undefined;
    let cancelled = false;
    fetch(API_ENDPOINTS.FORESIGHT_REPORT(instanceId), { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null)
      .then((body) => { if (!cancelled) setRuns(body?.scenarios || []); });
    return () => { cancelled = true; };
  }, [instanceId, want]);
  const available = new Set(FORESIGHT_SECTIONS.filter((id) => runs.some(HAS[id])));
  return { data: { runs }, available };
}

function Brief({ brief }) {
  const { t } = useTranslation();
  const rows = [
    brief.useType && [t('foresight.scope.useType'), t(`foresight.scope.useTypes.${brief.useType}`)],
    ...BRIEF_FIELDS.map((f) => brief[f] && [t(`foresight.scope.fields.${f}`), brief[f]]),
    [t('foresight.scope.horizon'), `${t('foresight.scope.horizonYears', { n: brief.horizonYears })}${brief.targetYear ? ` (${brief.targetYear})` : ''}`],
    brief.stakeholders?.length && [t('foresight.scope.fields.stakeholders'), brief.stakeholders.map((s) => s.name || s).join('\n')],
  ].filter(Boolean);
  return <Facts>{rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</Facts>;
}

function Drivers({ drivers }) {
  const { t } = useTranslation();
  return (
    <List>
      {drivers.filter((d) => d.status === 'approved').map((d) => (
        <Item key={d.id}>
          <div className="title">{d.title}</div>
          <div className="meta">{t(`foresight.drivers.categories.${d.category}`)} · {t(`foresight.drivers.impacts.${d.impact}`)}</div>
          {d.description && <p>{d.description}</p>}
          {d.sources?.length > 0 && (
            <div className="meta">{t('foresight.common.sources')}: {d.sources.map((s) => s.citation).filter(Boolean).join('; ')}</div>
          )}
        </Item>
      ))}
    </List>
  );
}

function Futures({ run }) {
  const { t } = useTranslation();
  const titles = new Map((run.drivers || []).map((d) => [String(d.id), d.title]));
  return (
    <List>
      {approvedFutures(run).map((f) => (
        <Item key={f.id} $accent={ARCHETYPE_ACCENT[f.archetype]}>
          <div className="meta">{t(`foresight.scenarios.archetypes.${f.archetype}`)}</div>
          <div className="title">{f.title}</div>
          {f.summary && <p><strong>{f.summary}</strong></p>}
          {f.narrative && <p>{f.narrative}</p>}
          {f.subjectView && <><SectionSubtitle>{t('foresight.scenarios.subjectView')}</SectionSubtitle><p>{f.subjectView}</p></>}
          {f.driverOutcomes?.length > 0 && (
            <>
              <SectionSubtitle>{t('foresight.scenarios.driverOutcomes')}</SectionSubtitle>
              <ul>{f.driverOutcomes.map((o) => <li key={o.driverId}><strong>{titles.get(String(o.driverId)) || '—'}:</strong> {o.outcome}</li>)}</ul>
            </>
          )}
          {['opportunities', 'risks'].map((k) => f[k]?.length > 0 && (
            <React.Fragment key={k}>
              <SectionSubtitle>{t(`foresight.scenarios.${k}`)}</SectionSubtitle>
              <ul>{f[k].map((x, i) => <li key={i}>{x}</li>)}</ul>
            </React.Fragment>
          ))}
        </Item>
      ))}
    </List>
  );
}

function PreferredFuture({ run }) {
  const { t } = useTranslation();
  const future = run.preferredFuture;
  const titles = new Map((run.futures || []).map((f) => [f.id, f.title]));
  return (
    <>
      <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{future.description}</Paragraph>
      {future.successConditions?.length > 0 && (
        <><SectionSubtitle>{t('foresight.preferred.successConditions')}</SectionSubtitle>
          <ul>{future.successConditions.map((c, i) => <li key={i}>{c}</li>)}</ul></>
      )}
      {future.valuesBasis && <><SectionSubtitle>{t('foresight.preferred.valuesBasis')}</SectionSubtitle><Paragraph>{future.valuesBasis}</Paragraph></>}
      {future.aiTensions?.length > 0 && (
        <><SectionSubtitle>{t('foresight.preferred.tensionsTitle')}</SectionSubtitle>
          <ul>{future.aiTensions.map((x, i) => (
            <li key={i}>{x.text}{x.resolved && <span style={{ color: SITRA.textMuted }}> ({t('foresight.preferred.tensionResolved')})</span>}</li>
          ))}</ul></>
      )}
      <List>
        {PATH_GROUPS.filter((g) => future[g]?.length).map((g) => (
          <Item key={g} $accent={GROUP_ACCENT[g]}>
            <div className="title">{t(`foresight.preferred.groups.${g}`)}</div>
            <ul>
              {future[g].map((f, i) => (
                <li key={i}>{f.text}{f.futureId && titles.get(f.futureId) ? <span className="meta"> ({titles.get(f.futureId)})</span> : null}</li>
              ))}
            </ul>
          </Item>
        ))}
      </List>
    </>
  );
}

/** A stage's items, its indicators (#1386, the target state's progress) first. */
const stageItems = (items, stage) => {
  const own = items.filter((i) => i.stage === stage);
  return [...own.filter((i) => i.kind === 'indicator'), ...own.filter((i) => i.kind !== 'indicator')];
};

function Path({ path: { path, items } }) {
  const { t } = useTranslation();
  return STAGES.filter((stage) => items.some((i) => i.stage === stage)).map((stage) => (
    <React.Fragment key={stage}>
      <SectionSubtitle>{stage}{path?.stageYears?.[stage] ? ` · ${path.stageYears[stage]}` : ''}</SectionSubtitle>
      <List>
        {stageItems(items, stage).map((i) => (
          <Item key={i.id} $accent={i.kind === 'turning_point' ? SITRA.orange : undefined}>
            <div className="meta">{t(`foresight.backcasting.kinds.${i.kind}`)}{i.kind === 'indicator' && i.dimension ? ` · ${i.dimension}` : ''}</div>
            <div className="title">{i.text}</div>
            {ITEM_DETAILS.filter((d) => i[d]).map((d) => (
              <div key={d} className="meta">{t(`foresight.backcasting.details.${d}`)}: {i[d]}</div>
            ))}
          </Item>
        ))}
      </List>
    </React.Fragment>
  ));
}

function StressTests({ run }) {
  const { t } = useTranslation();
  return (
    <List>
      {testedCards(run).map(({ id, title, description, stressTest: test }) => (
        <Item key={id} $accent={VERDICT_ACCENT[test.verdict]}>
          <div className="meta">{t(`foresight.wildcard.verdicts.${test.verdict}`)}</div>
          <div className="title">{title}</div>
          {description && <p>{description}</p>}
          {test.critique && <p>{test.critique}</p>}
          {test.changedItems?.length > 0 && (
            <>
              <SectionSubtitle>{t('foresight.wildcard.report.changedItems')}</SectionSubtitle>
              <ul>{test.changedItems.map((i) => <li key={i.id}>{i.stage} · {t(`foresight.backcasting.kinds.${i.kind}`)}: {i.text}</li>)}</ul>
            </>
          )}
          {test.decision && (
            <p><strong>{t(`foresight.wildcard.decisions.${test.decision}`)}.</strong> {test.decisionReason}</p>
          )}
        </Item>
      ))}
    </List>
  );
}

function Memory({ run }) {
  const { t } = useTranslation();
  const starting = startingAssumptions(run);
  const byFuture = approvedFutures(run).filter((f) => f.assumptions?.length);
  const rejected = [
    ...(run.drivers || []).filter((d) => d.status === 'rejected'),
    ...(run.futures || []).filter((f) => f.status === 'rejected'),
  ];
  const tested = testedCards(run);
  const signals = tested.flatMap((c) => c.stressTest.watchSignals || []);
  const years = run.path?.path?.stageYears || {};
  return (
    <>
      {starting.length > 0 && <><SectionSubtitle>{t('foresight.scope.assumptionsTitle')}</SectionSubtitle><ul>{starting.map((a, i) => <li key={i}>{a}</li>)}</ul></>}
      {byFuture.map((f) => (
        <React.Fragment key={f.id}>
          <SectionSubtitle>{t('foresight.report.scenarioAssumptions', { title: f.title })}</SectionSubtitle>
          <ul>{f.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </React.Fragment>
      ))}
      {tested.filter((c) => c.stressTest.revealedAssumptions?.length).map((c) => (
        <React.Fragment key={c.id}>
          <SectionSubtitle>{t('foresight.wildcard.report.revealedTitle', { title: c.title })}</SectionSubtitle>
          <ul>{c.stressTest.revealedAssumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </React.Fragment>
      ))}
      {signals.length > 0 && (
        <>
          <SectionSubtitle>{t('foresight.wildcard.report.signalsTitle')}</SectionSubtitle>
          <ul>
            {signals.map((w, i) => (
              <li key={i}>
                {w.checkpoint && <strong>{w.checkpoint}{years[w.checkpoint] ? ` · ${years[w.checkpoint]}` : ''}: </strong>}
                {w.signal}{w.indicator ? ` (${w.indicator})` : ''}{w.assumption ? ` — ${w.assumption}` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
      {rejected.length > 0 && (
        <>
          <SectionSubtitle>{t('foresight.report.rejectedTitle')}</SectionSubtitle>
          <ul>{rejected.map((x) => <li key={x.id}>{x.title}{x.rejectionReason ? ` — ${x.rejectionReason}` : ''}</li>)}</ul>
        </>
      )}
    </>
  );
}

const INTRO = {
  'foresight-brief': 'foresight.report.briefIntro',
  'change-drivers': 'foresight.report.driversIntro',
  'foresight-scenarios': 'foresight.report.scenariosIntro',
  'preferred-future': 'foresight.pathReport.preferredIntro',
  'backcasting-path': 'foresight.pathReport.pathIntro',
  'stress-test': 'foresight.wildcard.report.intro',
  'foresight-assumptions': 'foresight.report.assumptionsIntro',
};

const BLOCKS = {
  'foresight-brief': (run) => <Brief brief={run.brief} />,
  'change-drivers': (run) => <Drivers drivers={run.drivers} />,
  'foresight-scenarios': (run) => <Futures run={run} />,
  'preferred-future': (run) => <PreferredFuture run={run} />,
  'backcasting-path': (run) => <Path path={run.path} />,
  'stress-test': (run) => <StressTests run={run} />,
  'foresight-assumptions': (run) => <Memory run={run} />,
};

/**
 * @param {{ ids: string[], data: { runs: object[] }, available: Set<string>, show: (id) => boolean, accessOf: (id) => string }} props
 */
export default function ReportForesightSections({ ids, data, available: has, show, accessOf }) {
  const { t } = useTranslation();
  return ids.filter((id) => BLOCKS[id] && has.has(id) && show(id)).map((id) => (
    <SectionGate key={id} access={accessOf(id)} gateTitle={t(`report.nav.${FORESIGHT_NAV_KEYS[id]}`)}>
      <Section id={id}>
        <SectionTitle>{t(`report.nav.${FORESIGHT_NAV_KEYS[id]}`)}</SectionTitle>
        <Paragraph>{t(INTRO[id])}</Paragraph>
        {data.runs.filter(HAS[id]).map((run) => (
          <React.Fragment key={run.id}>
            <RunTitle>{run.title}</RunTitle>
            {BLOCKS[id](run)}
          </React.Fragment>
        ))}
      </Section>
      <Divider />
    </SectionGate>
  ));
}
